import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toAsiNormalImageUrl } from "../asiImageUtils";

const preloadImage = (src) => {
  if (!src) return;
  const img = new Image();
  img.decoding = "async";
  img.src = src;
};

const thumbButtonSx = {
  border: "1px solid #d7dde4",
  borderRadius: 2,
  cursor: "pointer",
  overflow: "hidden",
  position: "relative",
  width: 78,
  height: 78,
  flex: "0 0 auto",
  backgroundColor: "#fff",
};

const badgeSx = {
  position: "absolute",
  top: 6,
  left: 6,
  minWidth: 22,
  height: 22,
  px: 0.5,
  borderRadius: "999px",
  backgroundColor: "#c3171d",
  color: "#fff",
  display: "grid",
  placeItems: "center",
  fontSize: 11,
  fontWeight: 700,
};

const PromoStoreProductGalleryPreview = ({
  open,
  images = [],
  onClose,
}) => {
  const normalizedImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const imageCount = normalizedImages.length;

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
  }, [open, normalizedImages]);

  useEffect(() => {
    if (!open || imageCount <= 1) return;
    preloadImage(toAsiNormalImageUrl(normalizedImages[(activeIndex + 1) % imageCount]));
    preloadImage(toAsiNormalImageUrl(normalizedImages[(activeIndex - 1 + imageCount) % imageCount]));
  }, [activeIndex, imageCount, normalizedImages, open]);

  useEffect(() => {
    if (!open || imageCount <= 1) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + imageCount) % imageCount);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % imageCount);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [imageCount, open]);

  const currentImage = imageCount ? normalizedImages[activeIndex] : "";
  const currentImageSrc = toAsiNormalImageUrl(currentImage);

  const goPrev = () => setActiveIndex((prev) => (prev - 1 + imageCount) % imageCount);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % imageCount);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="promostore-gallery-preview-title"
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography id="promostore-gallery-preview-title" variant="h6" sx={{ fontWeight: 700 }}>
            Product Gallery
          </Typography>
          <IconButton onClick={onClose} aria-label="Close gallery preview">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: 3,
            background: "linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)",
            minHeight: 360,
            position: "relative",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            px: { xs: 2, sm: 7 },
            py: { xs: 2, sm: 4 },
          }}
        >
          {currentImageSrc ? (
            <img
              src={currentImageSrc}
              alt={`Product gallery image ${activeIndex + 1} of ${imageCount}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: 12,
                boxShadow: "0 20px 45px rgba(15, 23, 42, 0.12)",
                backgroundColor: "#fff",
              }}
            />
          ) : (
            <Typography color="text.secondary">No gallery images selected</Typography>
          )}

          {imageCount > 1 ? (
            <>
              <IconButton
                aria-label="Previous gallery image"
                onClick={goPrev}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#ffffffdd",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label="Next gallery image"
                onClick={goNext}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#ffffffdd",
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25, overflowX: "auto", mt: 2, pb: 0.5 }}>
          {normalizedImages.map((image, index) => {
            const src = toAsiNormalImageUrl(image);
            const selected = index === activeIndex;

            return (
              <Box
                key={`${image}-${index}`}
                role="button"
                tabIndex={0}
                aria-label={`Show gallery image ${index + 1}`}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActiveIndex(index);
                  }
                }}
                sx={{
                  ...thumbButtonSx,
                  borderColor: selected ? "#c3171d" : "#d7dde4",
                  boxShadow: selected ? "0 0 0 2px rgba(195, 23, 29, 0.12)" : "none",
                }}
              >
                <Box sx={badgeSx}>{index + 1}</Box>
                {src ? (
                  <img
                    src={src}
                    alt={`Gallery thumbnail ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Image
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {imageCount ? `Image ${activeIndex + 1} of ${imageCount}` : "No gallery images"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: activeIndex === 0 ? "#c3171d" : "#475569" }}>
            {activeIndex === 0 ? "Primary" : `Gallery image #${activeIndex + 1}`}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PromoStoreProductGalleryPreview;
